import Alert from '@amzn/awsui-components-core/alert';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Container from '@amzn/awsui-components-core/container';
import ContentLayout from '@amzn/awsui-components-core/content-layout';
import FormField from '@amzn/awsui-components-core/form-field';
import Header from '@amzn/awsui-components-core/header';
import Input from '@amzn/awsui-components-core/input';
import Select from '@amzn/awsui-components-core/select';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Spinner from '@amzn/awsui-components-core/spinner';
import Textarea from '@amzn/awsui-components-core/textarea';
import { FormikProvider, useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import ColumnsPicker, { ColumnEntry } from '../components/ColumnsPicker';
import RowsDimensionPicker, { DimensionItem } from '../components/RowsDimensionPicker';
import VariancePairsPicker, { AvailableScenario } from '../components/VariancePairsPicker';
import { createPnlReport, getPnlConfig } from '../services/starGridService';
import { PnlConfigResponse, PnlScenarioEntry, PnlSourceMode, PnlVariancePair } from '../starGridTypes';

/** Fact-table variants exposed on the Create Report form. Matches API AVAILABLE_SOURCE_MODES. */
const SOURCE_MODE_OPTIONS: Array<{ value: PnlSourceMode; label: string; description: string }> = [
  { value: 'INGESTED', label: 'Native (Ingested)', description: 'Fast queries; requires ingest job to keep fresh.' },
  { value: 'EXTERNAL', label: 'External (cdp_lake Glue)', description: 'Reads Glue/S3 live; slower cold scans, no ingest maintenance.' }
];

/** The virtual P&L statement-line dimension (computed from account_type; not in the config CSV). */
const STATEMENT_LINE_DIMENSION: DimensionItem = { label: 'P&L Statement Line', field: 'pnl_statement_line' };

/** Distinct dimension items (by fact column), plus the virtual statement-line dimension. */
function dimensionItems(config: PnlConfigResponse | undefined): DimensionItem[] {
  const byFactKey = new Map<string, DimensionItem>();
  for (const dim of config?.available_dimensions ?? []) {
    const factKey = dim.fact_key?.trim();
    if (!factKey || byFactKey.has(factKey)) {
      continue;
    }
    byFactKey.set(factKey, { label: dim.label || factKey, field: factKey });
  }
  return [STATEMENT_LINE_DIMENSION, ...byFactKey.values()];
}

/**
 * Shape of the Formik-owned form state (mirrors xPT's PnlReportFormValues).
 * The Yup schema below validates these exact paths so `errors.<path>` lights up
 * each FormField's errorText next to its input.
 */
export interface CreateReportFormValues {
  report_name: string;
  report_description: string;
  selected_dimensions: DimensionItem[];
  selected_filters: DimensionItem[];
  scenario_selections: ColumnEntry[];
  variance_pairs: PnlVariancePair[];
}

const REPORT_NAME_MIN = 4;
const REPORT_NAME_MAX = 64;
const DESCRIPTION_MAX = 500;
/** Allowed characters: letters, numbers, spaces, hyphens, underscores. */
const ALLOWED_CHARS_REGEX = /^[A-Za-z0-9 _-]+$/;

/** UI-facing form schema — same rules as xPT's createPnlReportUiFormSchema. */
const formSchema = Yup.object().shape({
  report_name: Yup.string()
    .required('Report name is required.')
    .min(REPORT_NAME_MIN, `Report name must be at least ${REPORT_NAME_MIN} characters.`)
    .max(REPORT_NAME_MAX, `Report name must be at most ${REPORT_NAME_MAX} characters.`)
    .matches(ALLOWED_CHARS_REGEX, `Use only letters, numbers, spaces, hyphens, and underscores (${REPORT_NAME_MIN}–${REPORT_NAME_MAX} characters).`),
  report_description: Yup.string().nullable().max(DESCRIPTION_MAX, `Description must be at most ${DESCRIPTION_MAX} characters.`),
  selected_dimensions: Yup.array().of(Yup.object()).min(1, 'Select at least one row dimension.').required('Select at least one row dimension.'),
  scenario_selections: Yup.array()
    .of(
      Yup.object({
        id: Yup.string().required(),
        scenario: Yup.mixed()
          .nullable()
          .test('scenario-required', 'Pick a scenario for this column.', (v) => v != null),
        selectedMonths: Yup.array().of(Yup.number().required()).min(1, 'Select at least one month for this column.')
      })
    )
    .min(1, 'Add at least one scenario column.')
    .required('Add at least one scenario column.'),
  variance_pairs: Yup.array()
    .of(
      Yup.object({
        base_scenario: Yup.string().required('Select a base scenario.'),
        compare_scenario: Yup.string().required('Select a comparison scenario.'),
        base_months: Yup.array().of(Yup.number().required()).min(1, 'Select at least one month pair.'),
        compare_months: Yup.array()
          .of(Yup.number().required())
          .min(1, 'Select at least one month pair.')
          .test('same-length', 'Base and comparison must have the same count of month pairs.', function (value) {
            return (value?.length ?? 0) === ((this.parent as PnlVariancePair).base_months?.length ?? 0);
          })
      })
    )
    .optional()
});

/** Return the field-level error only after the user has touched the field or attempted to submit. */
const displayError = (error: unknown, touched: unknown, submitCount: number): string | undefined => {
  const shouldShow = !!touched || submitCount > 0;
  if (!shouldShow) return undefined;
  if (typeof error === 'string') return error;
  return undefined;
};

export interface CreateReportViewProps {
  /** Called with the new report id after a successful create (parent refreshes list + navigates). */
  onCreated: (reportId: number) => void;
}

/**
 * PnL report builder (Admin → Create Report), mirroring the xPT POC's create form:
 * Report Details, Rows (drag-and-drop hierarchy), Filters, Columns (scenario × period),
 * and Variance Columns — plus the StarGrid measure select. Config-driven via
 * `GET /pnl-reports/config`; on submit POSTs to `/pnl-reports`, which synchronously
 * generates the report's StarRocks cube, then hands the new id back to the shell.
 */
export const CreateReportView: React.FC<CreateReportViewProps> = ({ onCreated }) => {
  const [config, setConfig] = useState<PnlConfigResponse | undefined>(undefined);
  const [configLoading, setConfigLoading] = useState<boolean>(true);
  const [configError, setConfigError] = useState<string | undefined>(undefined);
  const [measure, setMeasure] = useState<string>('measure_usd');
  const [sourceMode, setSourceMode] = useState<PnlSourceMode>('INGESTED');
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setConfigLoading(true);
    getPnlConfig()
      .then((cfg) => {
        if (cancelled) {
          return;
        }
        setConfig(cfg);
        if (cfg.available_measures.length > 0) {
          setMeasure((current) => (cfg.available_measures.includes(current) ? current : cfg.available_measures[0]));
        }
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setConfigError(caught instanceof Error ? caught.message : 'Unknown error');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setConfigLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const allDimensions = useMemo(() => dimensionItems(config), [config]);

  const formik = useFormik<CreateReportFormValues>({
    initialValues: {
      report_name: '',
      report_description: '',
      selected_dimensions: [],
      selected_filters: [],
      scenario_selections: [],
      variance_pairs: []
    },
    validationSchema: formSchema,
    onSubmit: async (values, helpers) => {
      setSubmitError(undefined);
      // ColumnEntry[] → API scenarios (one entry per column: scenario + its months).
      const scenarios: PnlScenarioEntry[] = values.scenario_selections
        .filter((c) => c.scenario !== null)
        .map((c) => ({
          reporting_scenario_id: c.scenario!.reporting_scenario_id,
          label: c.scenario!.scenario_name,
          months: c.selectedMonths,
          forecast_start_period: c.scenario!.forecast_start_period ?? null
        }));

      // Variance pairs reference columns by entry id in the UI; the API expects scenario names.
      const resolvedVariancePairs: PnlVariancePair[] = values.variance_pairs.map((p) => ({
        ...p,
        base_scenario: values.scenario_selections.find((c) => c.id === p.base_scenario)?.scenario?.scenario_name ?? p.base_scenario,
        compare_scenario: values.scenario_selections.find((c) => c.id === p.compare_scenario)?.scenario?.scenario_name ?? p.compare_scenario
      }));

      const trimmedDesc = values.report_description.trim();
      try {
        const created = await createPnlReport({
          report_name: values.report_name.trim(),
          report_description: trimmedDesc ? trimmedDesc : null,
          group_by_dimensions: values.selected_dimensions.map((d) => d.field),
          filter_by_dimension: values.selected_filters.map((d) => d.field),
          scenarios,
          measure,
          variance_pairs: resolvedVariancePairs,
          source_mode: sourceMode
        });
        onCreated(created.pnl_report_id);
      } catch (caught) {
        setSubmitError(caught instanceof Error ? caught.message : 'Unknown error');
      } finally {
        helpers.setSubmitting(false);
      }
    }
  });

  const { values, errors, touched, submitCount, isSubmitting, setFieldValue, setFieldTouched } = formik;

  const setValue = <K extends keyof CreateReportFormValues>(field: K, next: CreateReportFormValues[K]): void => {
    void setFieldValue(field as string, next, true);
    void setFieldTouched(field as string, true, false);
  };

  const nameError = displayError(errors.report_name, touched.report_name, submitCount);
  const descriptionError = displayError(errors.report_description, touched.report_description, submitCount);
  const dimensionsError = displayError(errors.selected_dimensions, touched.selected_dimensions, submitCount);
  const scenarioSelectionsError = typeof errors.scenario_selections === 'string' && submitCount > 0 ? errors.scenario_selections : undefined;

  // Columns with a chosen scenario are the domain of valid variance-pair endpoints.
  const varianceAvailableScenarios: AvailableScenario[] = useMemo(
    () =>
      values.scenario_selections
        .filter((c) => c.scenario !== null)
        .map((c) => ({ id: c.id, label: c.scenario!.scenario_name, selectedMonths: c.selectedMonths })),
    [values.scenario_selections]
  );

  const hasValidationErrors = submitCount > 0 && Object.keys(errors).length > 0;

  return (
    <ContentLayout
      header={
        <Header variant="h1" description="Define a P&L report — rows, filters, columns, and variance columns. Saving generates its StarRocks cube.">
          Create Report
        </Header>
      }
    >
      <SpaceBetween size="l">
        {configError && (
          <Alert type="error" header="Could not load report config">
            {configError}
          </Alert>
        )}

        {configLoading ? (
          <Box textAlign="center" padding="l">
            <Spinner /> Loading report config…
          </Box>
        ) : (
          <FormikProvider value={formik}>
            <SpaceBetween size="l">
              {/* Report Name + Description */}
              <div data-testid="stargrid-form-section-details">
                <Container header={<Header variant="h2">Report Details</Header>}>
                  <SpaceBetween size="l">
                    <FormField label="Report Name" description="A unique name to identify this report." errorText={nameError}>
                      <Input
                        value={values.report_name}
                        onChange={({ detail }) => setValue('report_name', detail.value)}
                        onBlur={() => void setFieldTouched('report_name', true)}
                        placeholder="e.g., DaS MEC Deck - Q3 2026"
                        invalid={!!nameError}
                        data-testid="stargrid-form-name-input"
                      />
                    </FormField>
                    <FormField
                      label={
                        <span>
                          Description <i>- optional</i>
                        </span>
                      }
                      description="A short summary of what this report shows."
                      errorText={descriptionError}
                    >
                      <Textarea
                        value={values.report_description}
                        onChange={({ detail }) => setValue('report_description', detail.value)}
                        onBlur={() => void setFieldTouched('report_description', true)}
                        placeholder="e.g., Monthly Actuals vs OP2 for the Q3 MEC deck, grouped by product and country."
                        rows={2}
                        invalid={!!descriptionError}
                        data-testid="stargrid-form-description-input"
                      />
                    </FormField>
                    <FormField
                      label="Fact source"
                      description="Which physical fact table this report reads from. Pinned at creation — to switch, create a new report."
                    >
                      <Select
                        selectedOption={SOURCE_MODE_OPTIONS.find((o) => o.value === sourceMode) ?? SOURCE_MODE_OPTIONS[0]}
                        options={SOURCE_MODE_OPTIONS}
                        onChange={({ detail }) => setSourceMode((detail.selectedOption?.value as PnlSourceMode) ?? 'INGESTED')}
                        data-testid="stargrid-form-source-mode-select"
                      />
                    </FormField>
                  </SpaceBetween>
                </Container>
              </div>

              {/* Rows (row hierarchy) */}
              <div data-testid="stargrid-form-section-rows">
                <Container
                  header={
                    <Header
                      variant="h2"
                      description="Drag dimensions into Rows to group by them. Drag within Rows to reorder — order sets the grouping hierarchy."
                    >
                      Rows
                    </Header>
                  }
                >
                  <FormField errorText={dimensionsError}>
                    <RowsDimensionPicker
                      allDimensions={allDimensions}
                      selectedDimensions={values.selected_dimensions}
                      onChange={(selected) => setValue('selected_dimensions', selected)}
                      isReadOnly={false}
                      testIdPrefix="stargrid-form-rows"
                    />
                  </FormField>
                </Container>
              </div>

              {/* Filters (report-time slicing) */}
              <div data-testid="stargrid-form-section-filters">
                <Container
                  header={
                    <Header
                      variant="h2"
                      description="Add global filters to the report - dimensions that are not added will not have their data granularity available but will improve report performance"
                    >
                      Filters
                    </Header>
                  }
                >
                  <RowsDimensionPicker
                    allDimensions={allDimensions}
                    selectedDimensions={values.selected_filters}
                    onChange={(selected) => setValue('selected_filters', selected)}
                    isReadOnly={false}
                    selectedLabel="Filters"
                    selectedDescription="Report will be filtered by these dimensions"
                    emptySelectedText="Drag dimensions here to filter the report."
                    availableDescription="Drag these to the left to add as filters in your report"
                    testIdPrefix="stargrid-form-filters"
                  />
                </Container>
              </div>

              {/* Columns (scenarios × months) */}
              <div data-testid="stargrid-form-section-columns">
                <Container
                  header={
                    <Header
                      variant="h2"
                      description="Add one column at a time. Each column pairs a scenario with a period. Drag the handle to reorder."
                    >
                      Columns
                    </Header>
                  }
                >
                  <FormField errorText={scenarioSelectionsError}>
                    <ColumnsPicker
                      availableScenarios={config?.available_scenarios ?? []}
                      columns={values.scenario_selections}
                      onChange={(next) => setValue('scenario_selections', next)}
                      isReadOnly={false}
                    />
                  </FormField>
                </Container>
              </div>

              {/* Variance Columns (optional) */}
              <div data-testid="stargrid-form-section-variance">
                <Container
                  header={
                    <Header variant="h2" description="Optionally add columns that show the delta between two scenarios for the same months.">
                      Variance Columns
                    </Header>
                  }
                >
                  <VariancePairsPicker
                    availableScenarios={varianceAvailableScenarios}
                    pairs={values.variance_pairs}
                    onChange={(next) => setValue('variance_pairs', [...next])}
                  />
                </Container>
              </div>

              {/* Measure (StarGrid-specific: the value column the cube aggregates) */}
              <div data-testid="stargrid-form-section-measure">
                <Container header={<Header variant="h2">Measure</Header>}>
                  <FormField label="Measure" description="The value column the report aggregates.">
                    <Select
                      selectedOption={{ label: measure, value: measure }}
                      onChange={({ detail }) => setMeasure(detail.selectedOption.value as string)}
                      options={(config?.available_measures ?? []).map((m) => ({ label: m, value: m }))}
                    />
                  </FormField>
                </Container>
              </div>

              {hasValidationErrors && (
                <Alert type="error" header="Fix the highlighted fields">
                  Some fields are missing or invalid — see the error messages next to each field.
                </Alert>
              )}

              {submitError && (
                <Alert type="error" header="Could not create report">
                  {submitError}
                </Alert>
              )}

              <Box>
                <Button variant="primary" loading={isSubmitting} onClick={() => void formik.submitForm()} data-testid="stargrid-form-submit">
                  Create Report
                </Button>
              </Box>
            </SpaceBetween>
          </FormikProvider>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
};

export default CreateReportView;
